import React from 'react';
import { Mail, Clock, Shield, AlertTriangle, Brain, HelpCircle, Bug, MessageSquare, AlertCircle } from 'lucide-react';

export const ContactUs = () => {
  return (
    <div className="min-h-screen bg-background py-12" data-testid="contact-us-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Mail className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Contact Us
          </h1>
          <p className="text-xl text-muted-foreground">
            We're here to help.
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-card rounded-2xl p-8 mb-8 border border-border">
          <p className="text-foreground leading-relaxed">
            If you have questions, feedback, or need assistance related to your account, subscriptions, 
            or platform features, please feel free to reach out to us via email. Our team reviews every 
            message carefully and responds as quickly as possible.
          </p>
        </div>

        {/* Email Support */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 mb-8 border border-primary/20">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-8 h-8 text-primary" />
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Email Support
            </h2>
          </div>
          <p className="text-foreground mb-4">For all inquiries, please contact us at:</p>
          <a 
            href="mailto:hearmeadm@gmail.com" 
            className="inline-flex items-center gap-2 text-2xl font-bold text-primary hover:underline"
            data-testid="support-email"
          >
            <Mail className="w-6 h-6" />
            hearmeadm@gmail.com
          </a>
        </div>

        {/* What We Can Help With */}
        <div className="bg-card rounded-2xl p-8 mb-8 border border-border">
          <h3 className="font-bold text-foreground mb-6">This includes:</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl">
              <HelpCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-foreground text-sm">Account and subscription support</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl">
              <Bug className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-foreground text-sm">Technical issues or bug reports</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl">
              <MessageSquare className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-foreground text-sm">Feedback or feature suggestions</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl">
              <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-foreground text-sm">Safety concerns or reporting inappropriate behavior</span>
            </div>
            <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl sm:col-span-2">
              <AlertCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-foreground text-sm">General questions about the platform</span>
            </div>
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-card rounded-2xl p-8 mb-8 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-primary" />
            <h3 className="font-bold text-foreground">Response Time</h3>
          </div>
          <p className="text-foreground">
            We aim to respond to all emails within <span className="font-semibold text-primary">24–48 business hours</span>. 
            Response times may vary during weekends or public holidays.
          </p>
        </div>

        {/* Important Note */}
        <div className="bg-yellow-500/10 rounded-2xl p-8 mb-8 border border-yellow-500/30">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <h3 className="font-bold text-yellow-400">Important Note</h3>
          </div>
          <ul className="space-y-3 text-foreground">
            <li className="flex items-start gap-2">
              <span className="text-yellow-400">•</span>
              We currently provide support via email only.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400">•</span>
              We do not offer phone or live chat support at this time.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400">•</span>
              <span>
                For <strong>medical emergencies</strong> or <strong>urgent mental health crises</strong>, 
                please contact your local emergency services or a qualified healthcare professional immediately. 
                Our support email is not monitored in real time and should not be used for emergency situations.
              </span>
            </li>
          </ul>
        </div>

        {/* Privacy */}
        <div className="bg-card rounded-2xl p-8 mb-8 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-green-400" />
            <h3 className="font-bold text-foreground">Your Privacy Matters</h3>
          </div>
          <p className="text-foreground">
            All communications sent to us are handled with confidentiality and care, 
            in accordance with our privacy and data protection practices.
          </p>
        </div>

        {/* Closing */}
        <div className="text-center bg-gradient-to-br from-primary/10 to-purple-500/10 rounded-2xl p-8 border border-primary/30">
          <Brain className="w-10 h-10 text-primary mx-auto mb-4" />
          <p className="text-foreground">
            We appreciate you taking the time to reach out and thank you for trusting us.
          </p>
        </div>
      </div>
    </div>
  );
};
